import { BlockEventType, BlockType } from "@sperrdatei/dto";

import { isBlockEventHistoryItem } from "../../utils/is-block-event-history-event";
import { isBlockEventHistoryWithUserNameItem } from "../../utils/is-block-event-history-with-username-event";
import { toLocalDate, toLocalDateTime } from "../../utils/to-local-date";

import type {
  BlockEvent,
  BlockEventHistory,
  BlockEventHistoryWithUserName,
} from "@sperrdatei/dto/types";
import type { JSX } from "react";

type BlockEventTypes =
  | BlockEvent[]
  | BlockEventHistory[]
  | BlockEventHistoryWithUserName[];

type BlockEventListProps<T extends BlockEventTypes> = {
  events: T;
  hideInstitute?: boolean;
  selectEvent?: (event: BlockEvent | BlockEventHistory) => void;
  deleteEvent?: (event: BlockEvent | BlockEventHistory) => void;
};

type NumberBadgeProps = {
  text: string;
  style?: "default" | "danger" | "warning";
};

const NumberBadge = ({ text, style }: NumberBadgeProps): JSX.Element => {
  const bgStyle =
    !style || style === "default" ? "bg-secondary" : `bg-${style}`;
  return (
    <span key={text} className={`badge ${bgStyle} test-dark small`}>
      {text}
    </span>
  );
};

export const BlockEventList = <T extends BlockEventTypes>({
  events,
  hideInstitute,
  selectEvent,
  deleteEvent,
}: BlockEventListProps<T>): JSX.Element | null => {
  return events.length ? (
    <div className="list-group mb-3 p-0">
      {events.map((event) => {
        return (
          <div
            key={event.prefix + event.number}
            className="list-group-item list-group-item-action d-flex"
            onClick={() => selectEvent && selectEvent(event)}
          >
            <div className="ms-2 me-auto w-100">
              <div className="fw-bold small text-start d-flex justify-content-between">
                <span>
                  {event.blockSize > 1
                    ? `Rufnummerblock ⇾ ${event.blockSize} Nummern`
                    : `Nummer`}
                </span>

                {isBlockEventHistoryItem(event) && !deleteEvent ? (
                  <span title={toLocalDateTime(event.created)}>
                    {toLocalDate(event.created)}
                  </span>
                ) : null}
              </div>
              <i
                title={
                  event.blockSize > 1
                    ? "Rufnummernblock löschen"
                    : "Nummer löschen"
                }
                className="bi bi-x-circle-fill focus-alert position-absolute"
                style={{
                  top: "15px",
                  right: "15px",
                  visibility: deleteEvent ? "visible" : "hidden",
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  if (deleteEvent) deleteEvent(event);
                }}
              />
              <div className="small text-start mb-1">
                {event.prefix}-{event.number}
              </div>
              {isBlockEventHistoryItem(event) ? (
                <div className="small text-start mb-1">
                  {hideInstitute ? (
                    <>
                      <strong>Erfasst am: </strong>
                      {toLocalDate(event.created)}
                    </>
                  ) : (
                    <strong>{event.institute}</strong>
                  )}

                  {isBlockEventHistoryWithUserNameItem(event) ? (
                    <span>
                      , <strong>{event.user}</strong>
                    </span>
                  ) : null}
                </div>
              ) : null}

              <div className="badge-list text-start mb-2">
                {isBlockEventHistoryItem(event) ? (
                  <NumberBadge
                    text={
                      event.eventType === BlockEventType.Block
                        ? "Gesperrt"
                        : "Freigegeben"
                    }
                    style={
                      event.eventType === BlockEventType.Block
                        ? "warning"
                        : "danger"
                    }
                  />
                ) : null}
                <NumberBadge
                  text={
                    event.isPrivate
                      ? "Privat: Alle Befragungen"
                      : event.blockType === BlockType.AllSurveys
                        ? "Gewerblich: Alle Befragungen"
                        : "Gewerblich: Bevölkerungsbefragungen"
                  }
                />
              </div>
              {event.comment ? (
                <>
                  {" "}
                  <div className="fw-bold small text-start">Bemerkung</div>
                  <div className="small text-start">{event.comment}</div>
                </>
              ) : (
                <div style={{ height: "41px" }}></div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  ) : null;
};
